import { useId, useState } from 'react'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Label } from '@/components/shadcn/label'
import { CheckList, type CheckListItem } from '@/components/common/CheckList'
import { FilterSearch } from '@/components/common/filters/FilterSearch'

interface SearchableCheckListProps {
  label: string
  searchLabel: string
  items: CheckListItem[]
  selected: (number | string)[]
  onChange: (ids: (number | string)[]) => void
  emptyText: string
  /** When set, a checkbox next to the label selects/deselects all currently visible items. */
  selectAllLabel?: string
  /** Extra classes for the label (callers with a different heading style). */
  labelClassName?: string
  /** Extra classes for the list box — mainly its height. */
  listClassName?: string
}

/**
 * Heading + search field + checkbox list, owning its own search state. Shared by every
 * side-by-side selection list (module assignment, group dialog) so they look and behave
 * identically.
 */
export function SearchableCheckList({
  label,
  searchLabel,
  items,
  selected,
  onChange,
  emptyText,
  selectAllLabel,
  labelClassName,
  listClassName,
}: SearchableCheckListProps) {
  const [search, setSearch] = useState('')
  const selectAllId = useId()

  const query = search.trim().toLowerCase()
  const visible = items.filter(i => i.id != null && (!query || i.label.toLowerCase().includes(query)))

  const visibleIds = visible.map(i => i.id!)
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selected.includes(id))

  const toggleAll = () => {
    onChange(
      allVisibleSelected
        ? selected.filter(id => !visibleIds.includes(id))
        : [...selected, ...visibleIds.filter(id => !selected.includes(id))],
    )
  }

  return (
    <div className="flex flex-1 flex-col gap-1.5">
      <div className="flex min-h-5 items-center justify-between gap-2">
        <Label className={labelClassName}>{label}</Label>
        {selectAllLabel && (
          <div className="flex items-center gap-1.5">
            <Checkbox
              id={selectAllId}
              checked={allVisibleSelected}
              disabled={visibleIds.length === 0}
              onCheckedChange={toggleAll}
            />
            <Label htmlFor={selectAllId} className="cursor-pointer text-xs font-normal text-muted-foreground">
              {selectAllLabel}
            </Label>
          </div>
        )}
      </div>
      <FilterSearch searchLabel={searchLabel} search={search} onChange={v => setSearch(v ?? '')} useDebounce={false} />
      <CheckList
        items={visible}
        selected={selected}
        onChange={onChange}
        emptyText={emptyText}
        className={listClassName ?? 'h-36 overflow-hidden rounded-md border p-2'}
      />
    </div>
  )
}
